import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Chip
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useSnackbar } from 'notistack';
import CodeSurface from './CodeSurface';
import { scopeLabel } from './apiTokenConstants';
import config from '../../config';

/**
 * One-time reveal of a freshly created or regenerated API token.
 * The plaintext only exists in this payload - once the dialog is
 * dismissed the secret is gone for good, so closing requires the
 * explicit "Done" button (no backdrop/escape dismissal).
 */
const TokenRevealDialog = ({ open, tokenPayload, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();

  if (!tokenPayload) return null;

  const isRegenerated = Boolean(tokenPayload.regenerated_at);

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      // Force an explicit dismissal so the secret isn't lost to a stray click
      disableEscapeKeyDown
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircleIcon color="success" />
          {isRegenerated ? 'Token Regenerated' : 'Token Created'}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Save this token securely - it won't be shown again.
          </Typography>
          <Typography variant="body2">
            {isRegenerated
              ? 'The previous secret has stopped working. Update any applications using this token.'
              : 'This is the only time the full token is displayed. Store it somewhere safe.'}
          </Typography>
        </Alert>

        <Typography variant="h6" gutterBottom>
          {tokenPayload.name}
        </Typography>

        <CodeSurface
          label={tokenPayload.token_id}
          code={tokenPayload.token}
          wrap
          onCopied={() => enqueueSnackbar('Token copied to clipboard', { variant: 'success' })}
          sx={{ mb: 2 }}
        />

        {/* The account's home-region API base — pair it with the token so
            integrations point at the right endpoint from the start. */}
        <CodeSurface
          label="API base URL"
          code={config.API_URL}
          onCopied={() => enqueueSnackbar('Base URL copied to clipboard', { variant: 'success' })}
          sx={{ mb: 0.5 }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Use this base URL with your token. Clients pointed at the default
          endpoint are redirected here automatically.
        </Typography>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
          <Box>
            <Typography
              variant="overline"
              sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, letterSpacing: 1 }}
            >
              Token ID
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: '"Fira Mono", "JetBrains Mono", "Menlo", monospace', fontSize: 12.5 }}>
              {tokenPayload.token_id}
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="overline"
              sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, letterSpacing: 1 }}
            >
              Expires
            </Typography>
            <Typography variant="body2">
              {tokenPayload.expires_at
                ? new Date(tokenPayload.expires_at).toLocaleString()
                : 'Never'}
            </Typography>
          </Box>
        </Box>

        <Box>
          <Typography
            variant="overline"
            sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, letterSpacing: 1 }}
          >
            Scopes
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
            {(tokenPayload.scopes || []).map((scope) => (
              <Chip
                key={scope}
                label={scope}
                title={scopeLabel(scope)}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: 11,
                  fontFamily: '"Fira Mono", "JetBrains Mono", "Menlo", monospace',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TokenRevealDialog;
